import { useState, useEffect } from "react";
import { useRef } from "react";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { canOpen, readStoredUser } from "../../utils/featureAccess";

import { ChannelSidebar } from "../../components/Channel";

import ChannelOverview from "./ChannelOverview";
import ChannelOverviewTsatDemo from "./ChannelOverviewTsatDemo";
import ChannelAvgActive from "./ChannelAvgActive";
import ChannelAvgActiveTsatDemo from "./ChannelAvgActiveTsatDemo";
import ChannelAvgActiveVw from "./ChannelAvgActiveVw";
import ChannelTotalViewersTr from "./ChannelTotalViewersTr";
import ChannelSwitchIn from "./ChannelSwitchIn";
import ChannelSwitchInTsatDemo from "./ChannelSwitchInTsatDemo";
import ChannelAvgVideo from "./ChannelAvgVideo";
import ChannelUniqueTv from "./ChannelUniqueTv";
import ChannelTuneInOut from "./ChannelTuneInOut";
import ChannelSegmentAnalysis from "./ChannelSegmentAnalysis";
import ChannelGridPage from "./ChannelGridPage";
import ChannelGridPagetv8 from "./ChannelGridPagetv8";
import ChannelGridPagenow from "./ChannelGridPagenow";
import ChannelSocialMediaCompare from "./ChannelSocialMediaCompare";
import ChannelCrossComparison from "./ChannelCrossComparison";
import ChannelManagementReport from "./ChannelManagementReport";
import ChannelChannelGridComparison from "./ChannelChannelGridComparison";
import ChannelLocation from "./ChannelLocation";
import ChannelPastPerformance from "./ChannelPastPerformance";
import YoutubeTrackerGrid from "./YoutubeTrackerGrid";
import ChannelLiveRanking from "./ChannelLiveRanking";
import ChannelShare from "./ChannelShare";
import { useChannelData } from "../../hooks/useChannelData";
import { useImpressionsData } from "../../hooks/useImpressionsData";
import { useBlurredFeatures } from "../../hooks/useBlurredFeatures";
import BlurredFeature from "../../components/BlurredFeature";
import { TTV_CHANNEL_ID } from "../../utils/ttvChannel";

import "./Channel.css";

export default function Channel() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // The page a link asks for, e.g. /channel/X?page=channelgrid.
  const requestedPage = (searchParams.get("page") || "").toLowerCase();

  const blurredFeatures = useBlurredFeatures(id);

  const withBlur = (key, element) =>
    blurredFeatures.includes(key) ? (
      <BlurredFeature>{element}</BlurredFeature>
    ) : (
      element
    );

  const [activeItem, setActiveItem] = useState(null);
  // On phones, sidebar starts closed (full-screen drawer opens on demand).
  // On desktop, it starts open.
  const isPhoneViewport = () =>
    typeof window !== "undefined" &&
    (window.innerWidth <= 768 || window.innerHeight <= 550);
  const [sidebarOpen, setSidebarOpen] = useState(
    typeof window !== "undefined" ? !isPhoneViewport() : true
  );
  const contentRef = useRef(null);

  // Readable labels for the current sidebar view, shared with the AI assistant.
  const VIEW_LABELS = {
    overview: "Overview",
    overviewtsatdemo: "Overview",
    avgactivetv: "Avg. Active TV",
    avgactivetvtsatdemo: "Avg. Active TV",
    avgactivevw: "Avg. Active TV",
    totalviewerstr: "Total Viewers Türkiye",
    switchin: "Switch-In",
    switchintsatdemo: "Switch-In",
    avgvidconsumption: "Avg. Video Consumption",
    uniquetv: "Unique TVs",
    tuneinout: "Tune-in/out",
    segmentanalysis: "Segment Analysis",
    channelgrid: "Channel Grid",
    channelgridtv8: "Channel Grid",
    channelgridnow: "Channel Grid",
    socialmediacompare: "Social Media Comparison",
    channelgridcomparison: "Channel Grid Comparison",
    crosscomparison: "Cross Comparison",
    channelshare: "Channel Share",
    managementreport: "Management Report",
    pastperformance: "Past Performance",
    youtubecompare: "YouTube Comparison",
    liveranking: "IntelTube live ranking",
    location: "Location",
  };

  const {
    enabled: refreshEnabled,
    setEnabled: setRefreshEnabled,
    intervalMin: refreshIntervalMin,
    setIntervalMin: setRefreshIntervalMin,
    countdown: refreshCountdown,
    refreshKey,
    manualRefresh,
  } = useAutoRefresh(1);

  const { channelInfo, rawVideo, loading, refreshing } = useChannelData(id, refreshKey);
  const { rawImpressions, loading: impressionsLoading, refreshing: impressionsRefreshing } = useImpressionsData(id, refreshKey);

  // TTV reference channel — only fetched for users the superadmin has granted
  // the TTV Overview flag to (User Management), and only when actually viewing
  // a page that uses it (Overview/Channel Grid/Avg. Active TV).
  const ttvEnabled = !!readStoredUser().ttvallowance;
  const needsTtvData = ttvEnabled && (activeItem === "channelgrid" || activeItem === "avgactivetv");
  const { rawVideo: ttvRawVideo } = useChannelData(needsTtvData ? TTV_CHANNEL_ID : null, refreshKey);
  useEffect(() => {
    if (!channelInfo || activeItem !== null) return;

    const channelItems = channelInfo.sidebarItems || channelInfo.sidebaritems || [];

    if (requestedPage) {
      const user = readStoredUser();

      // Links get forwarded, so one can name a channel the recipient does not
      // hold. Rather than a channel they cannot use, send them to the same
      // page on a channel of their own.
      const ownsChannel = (Array.isArray(user.channels) ? user.channels : [])
        .map(String)
        .includes(String(id));
      if (!ownsChannel) {
        navigate(`/go/${encodeURIComponent(requestedPage)}`, { replace: true });
        return;
      }

      // The menu's own rule, so a link can never open a page the sidebar would
      // not offer. If it fails, the channel opens as it normally does.
      if (canOpen(user.sidebaritems, channelItems, requestedPage)) {
        setActiveItem(requestedPage);
        return;
      }
    }

    const items = channelItems.map((s) => s.toLowerCase());

    if (items.includes("overviewtsatdemo")) {
      setActiveItem("overviewtsatdemo");
    } else {
      setActiveItem("overview");
    }
  }, [channelInfo, activeItem, requestedPage, id, navigate]);

  // A link to a channel that does not load at all — removed or deactivated —
  // still has a page worth reaching on one of the user's own channels.
  useEffect(() => {
    if (!loading && !channelInfo && requestedPage) {
      navigate(`/go/${encodeURIComponent(requestedPage)}`, { replace: true });
    }
  }, [loading, channelInfo, requestedPage, navigate]);


  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 300);
    return () => clearTimeout(timer);
  }, [activeItem]);

  // Re-fit charts when the sidebar opens/closes: the content width changes and
  // Syncfusion only resizes on a window "resize" event. Fire after the 0.3s
  // padding transition so the chart settles on the final width.
  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 350);
    return () => clearTimeout(timer);
  }, [sidebarOpen]);

  useEffect(() => {
    if (activeItem) {
      sessionStorage.setItem("adm_current_view", VIEW_LABELS[activeItem] || activeItem);
      // Notify RotateDeviceOverlay that the view changed
      window.dispatchEvent(new Event("viewChanged"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem]);

  useEffect(() => {
    if (!contentRef.current) return;

    const ro = new ResizeObserver(() => {
      window.dispatchEvent(new Event("resize"));
    });

    ro.observe(contentRef.current);

    return () => ro.disconnect();
  }, []);

  if (loading) return <div className="channel-loading">Loading...</div>;
  if (!channelInfo) return <div>Error loading channel</div>;

  // ✅ activeItem gelmeden render etme (çok önemli)
  if (!activeItem) return null;

  const storedUser = JSON.parse(localStorage.getItem("adm_panel_user") || "{}");

  const userSidebarItems =
    storedUser.sidebaritems?.map((s) => s.toLowerCase()) || [];

  const channelSidebarItems =
    (channelInfo.sidebarItems || channelInfo.sidebaritems || [])
      .map((s) => s.toLowerCase());

  // On phones, close the sidebar when user picks a menu item
  const handleSidebarItemClick = (item) => {
    setActiveItem(item);
    // Keep the address in step with the page on screen, so what is in the bar
    // can be copied and sent. Replaced rather than pushed: Back should leave
    // the channel, not step through every menu click.
    setSearchParams({ page: item }, { replace: true });
    if (isPhoneViewport()) {
      setSidebarOpen(false);
    }
  };

  return (
    <div
      className={`channel-page ${
        sidebarOpen ? "sidebar-open" : "sidebar-closed"
      }`}
    >
      <ChannelSidebar
        activeItem={activeItem}
        onItemClick={handleSidebarItemClick}
        userSidebarItems={userSidebarItems}
        channelSidebarItems={channelSidebarItems}
        sidebarOpen={sidebarOpen}
        onSidebarToggle={setSidebarOpen}
      />

      <div className="content" ref={contentRef}>
        {activeItem === "overview" && withBlur("overview", (
          <ChannelOverview
            channelId={id}
            refreshKey={refreshKey}
            channelInfo={channelInfo}
            rawVideo={rawVideo}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "overviewtsatdemo" && withBlur("overviewtsatdemo", (
          <ChannelOverviewTsatDemo
            channelId={id}
            refreshKey={refreshKey}
            channelInfo={channelInfo}
            rawVideo={rawVideo}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "avgactivetv" && withBlur("avgactivetv", (
          <ChannelAvgActive
            channelId={id}
            channelInfo={channelInfo}
            rawVideo={rawVideo}
            ttvRawVideo={ttvRawVideo}
            loading={loading}
            refreshing={refreshing}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "avgactivetvtsatdemo" && withBlur("avgactivetvtsatdemo", (
          <ChannelAvgActiveTsatDemo
            channelId={id}
            channelInfo={channelInfo}
            rawVideo={rawVideo}
            loading={loading}
            refreshing={refreshing}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "avgactivevw" && withBlur("avgactivevw", (
          <ChannelAvgActiveVw
            channelId={id}
            rawVideo={rawVideo}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "totalviewerstr" && withBlur("totalviewerstr", (
          <ChannelTotalViewersTr
            channelId={id}
            rawImpressions={rawImpressions}
            loading={impressionsLoading}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "switchin" && withBlur("switchin", (
          <ChannelSwitchIn
            channelId={id}
            rawImpressions={rawImpressions}
            loading={impressionsLoading}
            refreshing={impressionsRefreshing}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "switchintsatdemo" && withBlur("switchintsatdemo", (
          <ChannelSwitchInTsatDemo
            channelId={id}
            rawImpressions={rawImpressions}
            loading={impressionsLoading}
            refreshing={impressionsRefreshing}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "avgvidconsumption" && withBlur("avgvidconsumption", (
          <ChannelAvgVideo
            channelId={id}
            channelInfo={channelInfo}
            rawVideo={rawVideo}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "uniquetv" && withBlur("uniquetv", (
          <ChannelUniqueTv
            channelId={id}
            rawVideo={rawVideo}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "tuneinout" && withBlur("tuneinout", (
          <ChannelTuneInOut
            channelId={id}
            rawImpressions={rawImpressions}
            rawVideo={rawVideo}
            loading={loading || impressionsLoading}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "segmentanalysis" && withBlur("segmentanalysis", (
          <ChannelSegmentAnalysis
            channelId={id}
            rawVideo={rawVideo}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "channelgrid" && withBlur("channelgrid", (
          <ChannelGridPage
            channelId={id}
            rawVideo={rawVideo}
            ttvRawVideo={ttvRawVideo}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "channelgridtv8" && withBlur("channelgridtv8", (
          <ChannelGridPagetv8
            channelId={id}
            rawVideo={rawVideo}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "channelgridnow" && withBlur("channelgridnow", (
          <ChannelGridPagenow
            channelId={id}
            rawVideo={rawVideo}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "socialmediacompare" && withBlur("socialmediacompare", (
          <ChannelSocialMediaCompare
            channelId={id}
            channelInfo={channelInfo}
            rawVideo={rawVideo}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "channelgridcomparison" && withBlur("channelgridcomparison", (
          <ChannelChannelGridComparison
            channelId={id}
            rawVideo={rawVideo}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "crosscomparison" && withBlur("crosscomparison", (
          <ChannelCrossComparison
            channelId={id}
            rawVideo={rawVideo}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "managementreport" && withBlur("managementreport", (
          <ChannelManagementReport
            channelId={id}
            userSidebarItems={userSidebarItems}
            channelSidebarItems={channelSidebarItems}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "pastperformance" && withBlur("pastperformance", (
          <ChannelPastPerformance
            channelId={id}
            rawVideo={rawVideo}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "youtubecompare" && withBlur("youtubecompare", (
          <YoutubeTrackerGrid
            channelId={id}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
        {activeItem === "liveranking" && withBlur("liveranking", (
          <ChannelLiveRanking channelId={id} refreshKey={refreshKey} />
        ))}
        {activeItem === "channelshare" && withBlur("channelshare", (
          <ChannelShare channelId={id} />
        ))}
        {activeItem === "location" && withBlur("location", (
          <ChannelLocation
            channelId={id}
            refreshKey={refreshKey}
            refreshEnabled={refreshEnabled}
            setRefreshEnabled={setRefreshEnabled}
            refreshIntervalMin={refreshIntervalMin}
            setRefreshIntervalMin={setRefreshIntervalMin}
            refreshCountdown={refreshCountdown}
            manualRefresh={manualRefresh}
          />
        ))}
      </div>
    </div>
  );
}
